import React from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faAngleLeft,
  faCaretDown,
  faCaretRight,
  faCaretLeft,
  faCaretUp,
  faAngleRight,
  faAngleDown,
  faAngleUp,
  IconDefinition,
  faLanguage,
  faGear,
  faAddressCard,
  faChartArea,
  faRightFromBracket,
  faBars,
  faInbox,
  faPaperPlane,
  faFile,
  faCalendarDays,
  faBoxArchive,
  faHandshake,
  faClock as faClockSolid,
  faMessage,
  faPhoneAlt,
  faTimes,
  faDownload,
  faHeadset,
  faUser,
  faLock,
  faCalendar,
  faGraduationCap,
  faChalkboardTeacher,
  faCommentSlash,
  faChartPie,
  faTable,
  faStream,
  faChartBar,
  faInfoCircle,
  faComments,
  faBell,
  faShareNodes,
  faGlobeAmericas,
  faExclamationCircle,
  faUsersSlash,
  faMailBulk,
  faBullhorn,
  faPaperclip,
  faReply,
  faArchive,
  faVideoCamera,
  faVolumeXmark,
  faVolumeUp,
  faEllipsis,
  faCheck,
  faFileArrowUp,
  faExclamationTriangle,
  faCamera,
  faFileArrowDown,
  faTrash,
  faQuestionCircle,
  faMicrophone,
  faMicrophoneSlash,
  faRotate,
  faSort,
  faSortUp,
  faSortDown,
  faForward,
  faBackward,
  faSchool,
  faPenToSquare,
  faStar,
  faHome,
  faPlus,
  faEyeSlash,
  faSignInAlt,
  faTrashArrowUp,
  faPlayCircle,
  faBolt,
  faSearch,
  faShuffle,
  faPrint,
  faCheckSquare as faCheckSquareSolid,
  faSquareMinus as faSquareMinusSolid,
  faSquarePlus as faSquarePlusSolid,
  faEnvelopeOpen as faEnvelopeOpenSolid,
  faEnvelope as faEnvelopeSolid,
  faCirclePlay,
  faFlag,
  faFilter,
  faStickyNote,
  faLocationDot,
  faDotCircle,
  faCircle,
  faClipboardList,
  faEraser,
} from '@fortawesome/free-solid-svg-icons'
import {
  faEnvelope,
  faEnvelopeOpen,
  faCheckSquare,
  faSquare,
  faSquarePlus,
  faSquareMinus,
  faClock,
  faCalendarCheck,
} from '@fortawesome/free-regular-svg-icons'
import { SizeProp } from '@fortawesome/fontawesome-svg-core'
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons'
import { Tooltip } from '../components/simple/tooltip'

interface IC extends Omit<React.ComponentProps<typeof FontAwesomeIcon>, 'icon'> {
  /** An amount to thicken the icon stroke. */
  thicken?: number
  /** A tooltip to show when hovering over the icon. */
  tooltip?: React.ReactNode
  /** Whether to show the tooltip when its content is hovered. */
  allowHoveringTooltip?: boolean
  /** A function to call when the icon is hovered or unhovered. */
  onHoverChange?: (hovered: boolean) => void
  /** Whether to show the tooltip on mobile devices. */
  tooltipOnMobile?: boolean
}

interface CustomIconProps extends IC {
  icon: IconDefinition
}

/**
 * A custom icon component that wraps FontAwesomeIcon and adds extra functionality.
 */
const Icon = React.forwardRef<SVGSVGElement, CustomIconProps>(
  (
    { thicken = 0, tooltip, icon, className, onClick, allowHoveringTooltip, onHoverChange, tooltipOnMobile, ...props },
    ref
  ) => {
    const i = (
      <FontAwesomeIcon
        icon={icon}
        className={className}
        onClick={onClick}
        onMouseEnter={() => onHoverChange && onHoverChange(true)}
        onMouseLeave={() => onHoverChange && onHoverChange(false)}
        style={{ strokeWidth: thicken, fill: 'currentColor', stroke: 'currentColor' }}
        ref={ref}
        {...props}
      />
    )
    return tooltip ? (
      <Tooltip text={tooltip} allowHoveringContent={allowHoveringTooltip} tooltipOnMobile={tooltipOnMobile}>
        {i}
      </Tooltip>
    ) : (
      i
    )
  }
)

interface ImageIconProps extends React.ComponentProps<'span'> {
  /** The size of the icon. This is emulating the FontAwesome sizes, so see `sizeMap` if it is not working as expected. */
  size?: SizeProp
  /** A tooltip to show when hovering over the icon. */
  tooltip?: React.ReactNode
  /** Whether to show the tooltip when its content is hovered. */
  allowHoveringTooltip?: boolean
  /** A function to call when the icon is hovered or unhovered. */
  onHoverChange?: (hovered: boolean) => void
  /** Whether to show the tooltip on mobile devices. */
  tooltipOnMobile?: boolean
}

/**
 * A custom image icon component that creates a <Icon/> compatible element that displays an image instead of a font icon.
 */
const ImageIcon = React.forwardRef<HTMLSpanElement, ImageIconProps>(
  (
    { children, className, size = '1x', tooltip, allowHoveringTooltip, onHoverChange, tooltipOnMobile, ...props },
    ref
  ) => {
    // recreating the font awesome sizes as needed
    const sizeMap = {
      '2xs': '',
      xs: 'text-xs',
      sm: 'text-sm',
      lg: 'text-lg',
      xl: 'text-2xl',
      '2xl': 'text-3xl',
      '1x': '',
      '2x': '',
      '3x': '',
      '4x': '',
      '5x': '',
      '6x': '',
      '7x': '',
      '8x': '',
      '9x': '',
      '10x': '',
    }
    const icon = (
      <span
        className={`${className} ${sizeMap[size]}`}
        {...props}
        ref={ref}
        onMouseEnter={() => onHoverChange && onHoverChange(true)}
        onMouseLeave={() => onHoverChange && onHoverChange(false)}
      >
        {children}
      </span>
    )
    return tooltip ? (
      <Tooltip text={tooltip} allowHoveringContent={allowHoveringTooltip} tooltipOnMobile={tooltipOnMobile}>
        {icon}
      </Tooltip>
    ) : (
      icon
    )
  }
)

export const createImageIconComponent = (icon: React.ReactNode) =>
  React.forwardRef<HTMLSpanElement, Omit<ImageIconProps, 'children'>>((props, ref) => (
    <ImageIcon {...props} ref={ref}>
      {icon}
    </ImageIcon>
  ))

const createIconComponent = (icon: IconDefinition) =>
  React.forwardRef<SVGSVGElement, IC>((props, ref) => <Icon icon={icon} ref={ref} {...props} />)

export const IconAngleLeft = createIconComponent(faAngleLeft)
export const IconCaretDown = createIconComponent(faCaretDown)
export const IconCaretRight = createIconComponent(faCaretRight)
export const IconCaretRightCircle = createIconComponent(faCirclePlay)
export const IconAngleRight = createIconComponent(faAngleRight)
export const IconAngleDown = createIconComponent(faAngleDown)
export const IconLanguage = createIconComponent(faLanguage)
export const IconGear = createIconComponent(faGear)
export const IconAddressCard = createIconComponent(faAddressCard)
export const IconChartArea = createIconComponent(faChartArea)
export const IconLogOut = createIconComponent(faRightFromBracket)
export const IconHamburger = createIconComponent(faBars)
export const IconInbox = createIconComponent(faInbox)
export const IconSent = createIconComponent(faPaperPlane)
export const IconDraft = createIconComponent(faFile)
export const IconScheduled = createIconComponent(faClockSolid)
export const IconAttendance = createIconComponent(faCalendarDays)
export const IconArchived = createIconComponent(faBoxArchive)
export const IconHidden = createIconComponent(faEyeSlash)
export const IconConference = createIconComponent(faHandshake)
export const IconUnread = createIconComponent(faEnvelope)
export const IconUnreadSolid = createIconComponent(faEnvelopeSolid)
export const IconRead = createIconComponent(faEnvelopeOpen)
export const IconReadSolid = createIconComponent(faEnvelopeOpenSolid)
export const IconEmail = createIconComponent(faEnvelopeSolid)
export const IconText = createIconComponent(faMessage)
export const IconPhone = createIconComponent(faPhoneAlt)
export const IconTimes = createIconComponent(faTimes)
export const IconCaretUp = createIconComponent(faCaretUp)
export const IconAngleUp = createIconComponent(faAngleUp)
export const IconCaretLeft = createIconComponent(faCaretLeft)
export const IconFileDownload = createIconComponent(faFileArrowDown)
export const IconHeadset = createIconComponent(faHeadset)
export const IconUser = createIconComponent(faUser)
export const IconLock = createIconComponent(faLock)
export const IconCalendar = createIconComponent(faCalendar)
export const IconGraduationCap = createIconComponent(faGraduationCap)
export const IconTeacher = createIconComponent(faChalkboardTeacher)
export const IconCommentSlash = createIconComponent(faCommentSlash)
export const IconChartPie = createIconComponent(faChartPie)
export const IconTable = createIconComponent(faTable)
export const IconStream = createIconComponent(faStream)
export const IconChartBar = createIconComponent(faChartBar)
export const IconInfoCircle = createIconComponent(faInfoCircle)
export const IconComments = createIconComponent(faComments)
export const IconBell = createIconComponent(faBell)
export const IconCheckSquare = createIconComponent(faCheckSquare)
export const IconCheckSquareSolid = createIconComponent(faCheckSquareSolid)
export const IconSquare = createIconComponent(faSquare)
export const IconShare = createIconComponent(faShareNodes)
export const IconGlobe = createIconComponent(faGlobeAmericas)
export const IconWarningCircle = createIconComponent(faExclamationCircle)
export const IconTeacherOnly = createIconComponent(faUsersSlash)
export const IconEmailText = createIconComponent(faMailBulk)
export const IconAnnouncement = createIconComponent(faBullhorn)
export const IconPaperclip = createIconComponent(faPaperclip)
export const IconReply = createIconComponent(faReply)
export const IconArchive = createIconComponent(faArchive)
export const IconVideo = createIconComponent(faVideoCamera)
export const IconWhatsapp = createIconComponent(faWhatsapp)
export const IconAudio = createIconComponent(faVolumeUp)
export const IconStopAudio = createIconComponent(faVolumeXmark)
export const IconEllipses = createIconComponent(faEllipsis)
export const IconCheckMark = createIconComponent(faCheck)
export const IconFileUpload = createIconComponent(faFileArrowUp)
export const IconPhotoUpload = createIconComponent(faCamera)
export const IconWarningTriangle = createIconComponent(faExclamationTriangle)
export const IconTrash = createIconComponent(faTrash)
export const IconTrashRestore = createIconComponent(faTrashArrowUp)
export const IconQuestionCircle = createIconComponent(faQuestionCircle)
export const IconMicrophone = createIconComponent(faMicrophone)
export const IconMicrophoneSlash = createIconComponent(faMicrophoneSlash)
export const IconDownload = createIconComponent(faDownload)
export const IconReload = createIconComponent(faRotate)
export const IconSort = createIconComponent(faSort)
export const IconSortUp = createIconComponent(faSortUp)
export const IconSortDown = createIconComponent(faSortDown)
export const IconForward = createIconComponent(faForward)
export const IconBackwards = createIconComponent(faBackward)
export const IconSchool = createIconComponent(faSchool)
export const IconEdit = createIconComponent(faPenToSquare)
export const IconStar = createIconComponent(faStar)
export const IconHome = createIconComponent(faHome)
export const IconPlus = createIconComponent(faPlus)
export const IconSignIn = createIconComponent(faSignInAlt)
export const IconUnpause = createIconComponent(faPlayCircle)
export const IconSentArrow = createIconComponent(faRightFromBracket)
export const IconReceivedArrow = IconSignIn
export const IconLogins = createIconComponent(faBolt)
export const IconSearch = createIconComponent(faSearch)
export const IconSurvey = createIconComponent(faClipboardList)
export const IconAssessment = createIconComponent(faGraduationCap)
export const IconSwap = createIconComponent(faShuffle)
export const IconPrint = createIconComponent(faPrint)
export const IconPlusSquare = createIconComponent(faSquarePlus)
export const IconPlusSquareSolid = createIconComponent(faSquarePlusSolid)
export const IconMinusSquareSolid = createIconComponent(faSquareMinusSolid)
export const IconMinusSquare = createIconComponent(faSquareMinus)
export const IconFlag = createIconComponent(faFlag)
export const IconFilter = createIconComponent(faFilter)
export const IconClock = createIconComponent(faClock)
export const IconClockSolid = createIconComponent(faClockSolid)
export const IconLocation = createIconComponent(faLocationDot)
export const IconStickyNote = createIconComponent(faStickyNote)
export const IconBullet = createIconComponent(faCircle)
export const IconCalendarCheck = createIconComponent(faCalendarCheck)
export const IconFile = createIconComponent(faFile)
export const IconEraser = createIconComponent(faEraser)
export const IconCircle = createIconComponent(faDotCircle)

export const IconLexikeet = createImageIconComponent(<img src="/images/lk.png" height="20px" />)
